import {useEffect,useMemo,useState} from "react";
import {RefreshCcw, Search, LockKeyhole, Unlock, PackageCheck} from "lucide-react";
import "./AdminProductCycleAvailability.css";

export default function AdminProductCycleAvailability({api,currentUser}){
  const master=currentUser?.role==="master"||currentUser?.owner===true;
  const [data,setData]=useState({products:[],cycle_open:false,cycle_number:null});
  const [loading,setLoading]=useState(true),[pending,setPending]=useState(null);
  const [error,setError]=useState(""),[message,setMessage]=useState("");
  const [query,setQuery]=useState(""),[filter,setFilter]=useState("all");

  const load=async()=>{
    setLoading(true);
    try{
      const response=await api.get("/admin/product-cycle-availability");
      setData(response.data||{products:[],cycle_open:false});
      setError("");
    }catch(e){setError(e.response?.data?.detail||"Não foi possível consultar a disponibilidade dos produtos.");}
    finally{setLoading(false);}
  };
  useEffect(()=>{if(master)load();},[master]);

  const products=data.products||[];
  const counts=useMemo(()=>({
    all:products.length,
    open:products.filter(p=>!p.blocked).length,
    blocked:products.filter(p=>p.blocked).length,
    complete:products.filter(p=>p.complete_boxes>0).length,
    forming:products.filter(p=>p.forming_units>0).length,
    none:products.filter(p=>(p.order_count||0)===0&&(p.unit_units||0)===0&&(p.box_units||0)===0).length
  }),[products]);
  const visible=useMemo(()=>products.filter(p=>{
    if(filter==="blocked"&&!p.blocked)return false;
    if(filter==="open"&&p.blocked)return false;
    if(filter==="complete"&&!(p.complete_boxes>0))return false;
    if(filter==="forming"&&!(p.forming_units>0))return false;
    if(filter==="none"&&((p.order_count||0)>0||(p.unit_units||0)>0||(p.box_units||0)>0))return false;
    const q=query.trim().toLocaleLowerCase("pt-BR");
    return !q||(p.name+" "+p.category).toLocaleLowerCase("pt-BR").includes(q);
  }),[products,filter,query]);

  const bulkTargets=blocked=>products.filter(p=>
    (p.order_count||0)===0&&(p.unit_units||0)===0&&(p.box_units||0)===0&&
    p.blocked!==blocked
  );
  const changeBulk=async blocked=>{
    if(!data.cycle_open||pending||loading)return;
    const targets=bulkTargets(blocked);
    if(!targets.length)return;
    const action=blocked?"BLOQUEAR":"LIBERAR";
    const confirmed=window.confirm(
      action+" "+targets.length+" PRODUTO(S) SEM PEDIDOS do Pedido #"+data.cycle_number+
      "?\n\nA ação vale apenas para NOVAS compras. Nenhuma venda, BOX, cliente ou pagamento anterior será alterado.\n\n"+
      "Produtos que receberam pedidos desde a última atualização NÃO serão alterados; nesse caso, atualize a lista."
    );
    if(!confirmed)return;
    setPending("__bulk__");setError("");setMessage("");
    try{
      const response=await api.post("/admin/product-cycle-availability/bulk",{
        scope:"without_orders",blocked,cycle_id:data.cycle_id,
        product_ids:targets.map(p=>String(p.product_id))
      });
      // Refazer a leitura após o lote evita divergência em outra aba MASTER.
      const latest=await api.get("/admin/product-cycle-availability");
      setData(latest.data||{products:[],cycle_open:false});
      setMessage((response.data?.changed_count||0)+" produto(s) sem pedidos "+
        (blocked?"bloqueado(s)":"liberado(s)")+" no Pedido #"+data.cycle_number+".");
    }catch(e){
      setError(e.response?.data?.detail||
        "Não foi possível concluir o lote. Atualize a lista para conferir os estados.");
    }finally{setPending(null);}
  };

  const change=async product=>{
    if(!data.cycle_open||pending)return;
    setPending(product.product_id);setError("");setMessage("");
    try{
      const desired=!product.blocked;
      const response=await api.put("/admin/product-cycle-availability/"+encodeURIComponent(product.product_id),{blocked:desired});
      setData(previous=>({
        ...previous,products:(previous.products||[]).map(p=>p.product_id===product.product_id?
          {...p,blocked:response.data.blocked}:p)
      }));
      setMessage(product.name+(desired?" bloqueado para novas inclusões.":" liberado para novas inclusões."));
    }catch(e){
      setError(e.response?.data?.detail||"Não foi possível alterar a disponibilidade. Nenhuma mudança confirmada.");
    }finally{setPending(null);}
  };

  if(!master)return null;
  return <section className="pca-panel" aria-label="Disponibilidade de produtos por pedido">
    <div className="pca-head">
      <div>
        <p className="eyebrow">FECHAMENTOS · CONTROLE DO MASTER</p>
        <h2>Disponibilidade por produto — Pedido #{data.cycle_number||"—"}</h2>
        <p>Use o interruptor de cada item. <strong>Verde = recebe novos pedidos</strong>; <strong>vermelho = bloqueado</strong>. Isso não fecha o pedido geral nem altera reservas existentes.</p>
      </div>
      <button type="button" className="pca-refresh" onClick={load} disabled={loading||!!pending} aria-label="Atualizar produtos"><RefreshCcw size={16}/> Atualizar</button>
    </div>

    <div className="pca-stats" aria-label="Resumo da disponibilidade">
      <div><strong>{counts.all}</strong><span>Produtos</span></div>
      <div className="pca-stat-open"><strong>{counts.open}</strong><span>Liberados</span></div>
      <div className="pca-stat-closed"><strong>{counts.blocked}</strong><span>Bloqueados</span></div>
      <div><strong>{counts.complete}</strong><span>Com BOX completo</span></div>
    </div>

    {!data.cycle_open&&!loading&&<p className="pca-alert" role="status">O pedido geral não está aberto. Os interruptores estão desativados para preservar o histórico.</p>}
    {error&&<p className="pca-error" role="alert">{error}</p>}
    {message&&<p className="pca-success" role="status">{message}</p>}
    <div className="pca-controls">
      <label className="pca-search"><Search size={17}/><input value={query} onChange={e=>setQuery(e.target.value)} placeholder="Buscar produto..." aria-label="Buscar produto"/></label>
      <label className="pca-select">Mostrar <select value={filter} onChange={e=>setFilter(e.target.value)} aria-label="Filtrar produtos">
        <option value="all">Todos ({counts.all})</option>
        <option value="open">Liberados ({counts.open})</option>
        <option value="blocked">Bloqueados ({counts.blocked})</option>
        <option value="complete">Com BOX completo ({counts.complete})</option>
        <option value="forming">Em formação ({counts.forming})</option>
        <option value="none">Sem pedidos ({counts.none})</option>
      </select></label>
    </div>
    {filter==="none"&&!loading&&<div className="pca-bulk-bar">
      <div className="pca-bulk-heading">
        <strong>Controle em lote — produtos sem pedidos</strong>
        <small>{counts.none} produto(s) com zero pedidos por frasco e zero BOX ATACADO neste ciclo.
          Você decide quando bloquear ou liberar; não há ação automática.</small>
      </div>
      <div className="pca-bulk-buttons">
        <button type="button" className="pca-bulk-block" disabled={!data.cycle_open||!!pending||bulkTargets(true).length===0}
          onClick={()=>changeBulk(true)}>
          <LockKeyhole size={15}/> Bloquear {bulkTargets(true).length} sem pedidos
        </button>
        <button type="button" className="pca-bulk-release" disabled={!data.cycle_open||!!pending||bulkTargets(false).length===0}
          onClick={()=>changeBulk(false)}>
          <Unlock size={15}/> Liberar {bulkTargets(false).length} sem pedidos
        </button>
      </div>
    </div>}
    {loading?<p className="pca-empty">Carregando produtos...</p>:
      !visible.length?<p className="pca-empty">Nenhum produto corresponde ao filtro.</p>:
      <div className="pca-list">
        {visible.map(p=>{
          const total=Math.max(0,Number(p.unit_units)||0);
          const complete=Math.floor(total/10),remainder=total%10;
          const progress=total>0&&remainder===0?10:remainder;
          const changing=pending===p.product_id;
          return <div className={"pca-item "+(p.blocked?"pca-item-locked":"")} key={p.product_id}>
            <div className="pca-product">
              <strong>{p.name}</strong>
              <small>{total} frasco(s) · {complete} BOX completo(s){remainder?" · "+remainder+"/10 no próximo BOX":""}</small>
              {p.box_units>0&&<small>BOX ATACADO: {p.box_units/10} BOX(es) já registrados (preservados)</small>}
              {p.catalog_available===false&&<small className="pca-catalog-warning">Produto também está indisponível no cadastro geral.</small>}
            </div>
            <div className="pca-progress"><span>{progress}/10</span>
              <div className="pca-track" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={10} aria-label={"Progresso do BOX de "+p.name}><i style={{width:progress*10+"%"}}/></div>
            </div>
            <div className="pca-switch-wrap">
              <span className={"pca-state "+(p.blocked?"is-locked":"is-open")}>{p.blocked?<LockKeyhole size={13}/>:<PackageCheck size={13}/>} {p.blocked?"BLOQUEADO":"LIBERADO"}</span>
              <button type="button" role="switch" aria-checked={!p.blocked}
                aria-label={(p.blocked?"Liberar ":"Bloquear ")+p.name+" para novas compras do Pedido #"+data.cycle_number}
                title={p.blocked?"Liberar novas compras":"Bloquear novas compras"}
                className={"pca-toggle "+(p.blocked?"pca-off":"pca-on")}
                disabled={loading||!!pending||!data.cycle_open}
                onClick={()=>change(p)}>
                <span className="pca-toggle-word">{p.blocked?"OFF":"ON"}</span>
                <span className="pca-toggle-knob"/>
              </button>
              {changing&&<small>Salvando...</small>}
            </div>
          </div>;
        })}
      </div>}
    <p className="pca-note"><Unlock size={15}/> O bloqueio vale apenas para o pedido atual. Ao abrir o próximo pedido, todos os produtos começam liberados neste controle. Clientes, quantidades, BOXes anteriores, valores e pagamentos não são alterados.</p>
  </section>;
}
